// ENG-8163 follow-up: measure the DEPLOYED project/CLAUDE.md and report it on
// the per-agent heartbeat.
//
// Why this exists. ENG-8105 put a size guard on CLAUDE.md, but it measures the
// GENERATED artifact (`provision/CLAUDE.md`) in CI. What Claude Code actually
// loads is `project/CLAUDE.md` — the generated body PLUS whatever the manager
// grafts on afterwards. Nothing measured that file, and on 2026-07-27 an SSM
// audit of agt-aws-1 found ALL 14 active agents over the threshold (scout
// 51,059, stirling 51,040, maven 50,523). Worse, the audit caught it still
// growing: pepper went 48,772 -> 51,115 in an hour as skills were added.
//
// A CI guard structurally cannot catch this: the appended content is DB-driven
// and per-agent, so only the host knows the real size. Hence a host-side
// measurement on every heartbeat.
//
// ENG-9459 — WHAT BEING "OVER" MEANS, AND WHAT IT DOES NOT. This module used to
// say the agent was "silently losing the tail of its own system prompt". That
// was measured false: Claude Code does not truncate CLAUDE.md at ~40,000 chars
// on either path we use (`--system-prompt-file`, and the cwd project autoload).
// Canaries at char 129,463 and char 250,065 were both recited back verbatim.
// See the size-budget block in
// packages/core/src/provisioning/frameworks/claudecode/identity.ts for the full
// measurement before reintroducing any truncation claim here.
//
// Being over threshold is a CONTEXT COST, not data loss: the whole file is
// re-sent on every request, so an oversized one permanently spends window the
// agent would otherwise use for work.
//
// ENG-9466 - CLAUDE.md IS NOT THE WHOLE RESIDENT COST. The generated
// `.claude/skills/core-knowledge/SKILL.md` carries a `description:` frontmatter
// line, and a skill description is resident in context for the entire session
// (it is how the model decides whether to invoke the skill). It used to
// enumerate every knowledge title, uncapped: on the prod fleet the largest
// agent carried 92,828 chars there on top of a 134,208-char CLAUDE.md.
// Measuring only CLAUDE.md would have let the fleet alarm return to OK with
// ~41% of that agent's resident cost still in place. So `residentChars` is the
// number that matters, and `chars` is retained unchanged so the existing
// per-agent series stays comparable across the change.
//
// The reported `ceiling` still travels WITH the measurement, but is now
// DIAGNOSTIC only — it records what this host compared against. The alarm
// threshold is resolved API-side from the shared core constant (ENG-9459), so
// correcting it does not have to wait for the whole fleet to self-update.

import { readFileSync } from 'node:fs';
import { join } from 'node:path';
import { CLAUDE_MD_CONTEXT_ALARM_CHARS } from '@augmented/core';

export interface ClaudeMdSizeReport {
  /** Characters, matching CLAUDE_MD_CONTEXT_ALARM_CHARS semantics (JS string length). */
  chars: number;
  /** Bytes on disk. Reported alongside chars because they diverge on non-ASCII
   *  content, and a byte count alone reads as a false breach (~250-350 above
   *  chars in the observed prod files). */
  bytes: number;
  /**
   * ENG-9466: characters in the `description:` frontmatter of the generated
   * `.claude/skills/core-knowledge/SKILL.md`, or 0 when the agent has no such
   * skill. Resident for the whole session, exactly like CLAUDE.md.
   *
   * 0 is a real reading here (no skill / no description), unlike a null
   * report, which means the CLAUDE.md read itself failed.
   */
  skillDescriptionChars: number;
  /**
   * chars + skillDescriptionChars — the always-in-context total. THIS is what
   * the ceiling is about; `chars` alone understates it.
   */
  residentChars: number;
  /** The ceiling this measurement was taken against — see the note above. */
  ceiling: number;
  /**
   * residentChars > ceiling. Precomputed so a consumer never re-derives it
   * wrongly — and note it is resident, not `chars`, so an agent can be over
   * while its CLAUDE.md alone is under.
   */
  overCeiling: boolean;
}

/**
 * Measure an agent's deployed `project/CLAUDE.md`.
 *
 * Returns null when the file is absent or unreadable — a fresh/never-provisioned
 * agent, an opencode agent, or a transient read fault. Null means "no signal",
 * NOT "fine": consumers must skip it rather than write a passing 0, or a
 * broken read would silently OK-transition the alarm.
 *
 * One read per agent per heartbeat (~40KB x N agents per ~90s) — negligible,
 * and cheaper than statting then reading, since chars require the content.
 */
export function measureClaudeMd(
  configDir: string,
  codeName: string,
  ceiling: number = CLAUDE_MD_CONTEXT_ALARM_CHARS,
): ClaudeMdSizeReport | null {
  let chars: number;
  let bytes: number;
  try {
    const buf = readFileSync(join(configDir, codeName, 'project', 'CLAUDE.md'));
    chars = buf.toString('utf8').length;
    bytes = buf.length;
  } catch {
    return null;
  }

  const skillDescriptionChars = measureCoreKnowledgeDescription(configDir, codeName);
  const residentChars = chars + skillDescriptionChars;
  return {
    chars,
    bytes,
    skillDescriptionChars,
    residentChars,
    ceiling,
    overCeiling: residentChars > ceiling,
  };
}

/**
 * ENG-9466: length of the `description:` frontmatter line in the generated
 * core-knowledge skill.
 *
 * Returns 0 when the skill, the frontmatter, or the key is absent — all of
 * which genuinely mean "no description cost". A read fault also yields 0
 * rather than sinking the whole report: CLAUDE.md is the primary signal and
 * losing it to a missing sidecar would be a worse trade than under-reporting
 * this addend. The under-report is bounded and one-directional, so it can
 * never manufacture a breach that is not there.
 *
 * Only the first `description:` at column 0 counts, and only inside a CLOSED
 * `---` block. Both halves matter: this skill's body IS the knowledge base —
 * megabytes of user prose that can easily contain a line starting with
 * `description:` — so a measurement that could reach the body would let
 * arbitrary user content set it. An UNCLOSED block therefore measures 0: it is
 * malformed, there is no frontmatter to speak of, and the alternative is a
 * scan that runs to EOF through exactly that prose. This keeps the whole
 * function one-directional — it can under-report a real cost, never invent one.
 */
function measureCoreKnowledgeDescription(configDir: string, codeName: string): number {
  let text: string;
  try {
    text = readFileSync(
      join(configDir, codeName, 'project', '.claude', 'skills', 'core-knowledge', 'SKILL.md'),
      'utf8',
    );
  } catch {
    return 0;
  }

  const lines = text.split('\n');
  if (lines[0]?.trim() !== '---') return 0;

  let descriptionChars: number | undefined;
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    // Return on the CLOSING delimiter, not on the `description:` line. That
    // ordering is the body-scan protection: a description is only reported
    // once the block it sits in has been proven to be frontmatter.
    if (line.trim() === '---') return descriptionChars ?? 0;
    if (descriptionChars === undefined && line.startsWith('description:')) {
      descriptionChars = line.length;
    }
  }
  return 0; // unclosed — malformed, so nothing here is frontmatter
}

/**
 * Transition-only WARN state. Logging every heartbeat would emit the same line
 * ~every 90s per over-ceiling agent forever — the ENG-6478 env-mask-warn
 * mistake. Warn when an agent CROSSES into breach, and once more when it
 * recovers, so the log carries the edges rather than a standing drone.
 */
const lastOverCeiling = new Map<string, boolean>();

/** Exported for tests — resets the transition-warn memo. */
export function resetClaudeMdWarnState(): void {
  lastOverCeiling.clear();
}

/**
 * Emit a WARN on the over-ceiling edges. Returns the line logged, or null when
 * nothing changed (so callers/tests can assert the debounce).
 */
export function logClaudeMdCeilingTransition(
  codeName: string,
  report: ClaudeMdSizeReport | null,
  log: (msg: string) => void,
): string | null {
  // No signal — leave the memo untouched so a transient unreadable file does
  // not fake a recovery and re-arm the warn.
  if (!report) return null;

  const previous = lastOverCeiling.get(codeName);
  // CodeRabbit (#3806): seed the FIRST healthy observation silently. Without
  // this, `undefined !== false` makes every under-ceiling agent log "back under
  // the ceiling" on every manager restart — a recovery notice for a breach that
  // never happened, once per agent per restart. A first observation that is
  // already OVER still warns: that one is real and needs to be seen.
  if (previous === undefined && !report.overCeiling) {
    lastOverCeiling.set(codeName, false);
    return null;
  }
  if (previous === report.overCeiling) return null;
  lastOverCeiling.set(codeName, report.overCeiling);

  const line = report.overCeiling
    ? `[claude-md] '${codeName}' resident identity is ${report.residentChars} chars (CLAUDE.md ${report.chars} + core-knowledge skill description ${report.skillDescriptionChars}), OVER the ${report.ceiling} threshold by ${report.residentChars - report.ceiling} — nothing is truncated, but both are re-sent every request, so this is context permanently spent (ENG-8163 / ENG-9459 / ENG-9466)`
    : `[claude-md] '${codeName}' resident identity back under the threshold (${report.residentChars}/${report.ceiling} chars)`;
  log(line);
  return line;
}
